/**
 * Chart tab (screenshots/72): search, "Fits this data" shortcuts, the 25-type picker with its hover card (81 #1–3, #10),
 * and the variants for the chosen type (bar sort / top N / Others / corners, tree direction + polyline, clusters k).
 */
import { useState } from "react";
import { CIcon } from "../icons";
import { TypePicker } from "../TypePicker";
import { CHART_TYPES, fits, pickerGroups, type ChartConfig, type ChartType, type Shape } from "../chartTypes";
import { DATASETS, type ChartSettings } from "../chartSettings";
import { useChartSettings, type ChartSettingsStore } from "../chartSettingsStore";
import type { ChartTheme } from "../themes";
import { Switch } from "../../../components/shared/ui";
import { Row, Section, Seg } from "./ui";
import { TableSettingsPanel } from "./TableTab";

export function ChartTab({ store, shape, sample, theme, onAnnounce }: { store: ChartSettingsStore; shape: Shape; sample?: boolean; theme?: ChartTheme; onAnnounce: (s: string) => void }) {
  const s = useChartSettings(store, (x) => x.settings);
  const [q, setQ] = useState("");
  const fitting = pickerGroups().flatMap((g) => g.types).filter((t) => fits(t, shape));
  const shortcuts = [s.type, ...fitting.filter((t) => t !== s.type)].filter((t) => fits(t, shape)).slice(0, 4);
  const choose = (t: ChartType) => { store.getState().update((x) => ({ ...x, type: t }), `Type: ${CHART_TYPES[t].label}`); onAnnounce(`Chart type ${CHART_TYPES[t].label}. Preview updated.`); };
  const ds = DATASETS[s.data.datasetId];
  const wardField = ds.fields.find((f) => f.type === "ward");

  // 81 #10: a dimmed type offers a one-click switch to data that works, instead of an error after the fact.
  const fix = (t: ChartType) => {
    if (t === "wardMap" && wardField) {
      store.getState().update((x) => ({ ...x, type: "wardMap", data: { ...x.data, mapping: { ...x.data.mapping, category: wardField.id }, topN: 50, groupRest: false } }), "Group by ward");
      onAnnounce("Grouped by ward. Chart type Ward map.");
    } else store.getState().setTab("data");
  };

  // 87: a Table's settings replace the picker; "Change type" brings the picker back.
  const [picking, setPicking] = useState(false);
  if (s.type === "table" && !picking) {
    const rows = shape.kind === "time" ? shape.points.length : "rows" in shape ? (shape.rows as unknown[]).length : 0;
    return <TableSettingsPanel store={store} s={s} rows={rows} onChangeType={() => setPicking(true)} />;
  }
  return (
    <div className="cs-sections">
      <Section title="Chart type">
        <label className="cs-search"><CIcon name="search" /><input type="search" placeholder={`Search ${Object.keys(CHART_TYPES).length} chart types`} aria-label="Search chart types" value={q} onChange={(e) => setQ(e.target.value)} /></label>
        <div className="cs-fits"><CIcon name="wand-sparkles" className="cs-fits__icon" /><span>Fits this data:</span>
          {shortcuts.map((t) => (
            <button key={t} type="button" className={`cs-fit${t === s.type ? " is-on" : ""}`} aria-pressed={t === s.type} onClick={() => choose(t)}>
              {t === s.type && <CIcon name="check" size={12} />}{CHART_TYPES[t].label}</button>
          ))}
        </div>
        <TypePicker shape={shape} sample={sample} value={s.type} theme={theme} query={q}
          onChange={choose}
          onTry={(t) => { store.getState().setPreview(t ? { type: t } : null); onAnnounce(t ? `Trying ${CHART_TYPES[t].label} in the widget. Enter keeps it, Escape reverts.` : "Reverted."); }}
          onFix={fix} fixLabel={(t) => (t === "wardMap" && wardField ? "Group by ward instead" : null)} />
      </Section>
      <TypeOptions store={store} s={s} shape={shape} />
    </div>
  );
}

function TypeOptions({ store, s, shape }: { store: ChartSettingsStore; s: ChartSettings; shape: Shape }) {
  const setData = (patch: Partial<ChartSettings["data"]>, label: string) => store.getState().update((x) => ({ ...x, data: { ...x.data, ...patch } }), label);
  const setConfig = (patch: Partial<ChartConfig>, label: string) => store.getState().update((x) => ({ ...x, config: { ...x.config, ...patch } }), label);
  const t = s.type;
  if (t === "bar" || t === "column") {
    const n = s.data.topN ?? 8;
    return (
      <Section title="Bar options">
        <Row label="Orientation">
          <Seg label="Orientation" value={t === "bar" ? "h" : "v"} options={[{ id: "h", label: "Horizontal" }, { id: "v", label: "Vertical" }]}
            onChange={(o) => { const next: ChartType = o === "h" ? "bar" : "column"; if (fits(next, shape) || next === "bar") store.getState().update((x) => ({ ...x, type: next }), `Type: ${CHART_TYPES[next].label}`); }} />
        </Row>
        <Row label="Sort">
          <Seg label="Sort" value={s.data.sort} onChange={(sort) => setData({ sort }, "Sort")}
            options={[{ id: "valueDesc", label: <>Value <span aria-hidden>↓</span></> }, { id: "valueAsc", label: <>Value <span aria-hidden>↑</span></> }, { id: "az", label: "A–Z" }, { id: "none", label: "As loaded" }]} />
        </Row>
        <Row label="Show">
          <span className="cs-stepper" role="group" aria-label="Rows shown">
            <button type="button" aria-label="Show fewer" disabled={n <= 1} onClick={() => setData({ topN: Math.max(1, n - 1) }, `Top ${n - 1}`)}><CIcon name="minus" size={12} /></button>
            <output aria-live="polite">Top {n}</output>
            <button type="button" aria-label="Show more" disabled={n >= 50} onClick={() => setData({ topN: Math.min(50, n + 1) }, `Top ${n + 1}`)}><CIcon name="plus" size={12} /></button>
          </span>
          <Switch on={s.data.groupRest} label="Group the rest as “Others”" onChange={(groupRest) => setData({ groupRest }, groupRest ? "Others on" : "Others off")} />
          <span className="cs-row__text">Group the rest as “Others”</span>
        </Row>
        <Row label="Corners">
          <Seg label="Corners" value={s.config.corners ?? "soft"} onChange={(corners) => setConfig({ corners }, "Corners")}
            options={[{ id: "square", label: "Square" }, { id: "soft", label: "Soft" }, { id: "round", label: "Round" }]} />
        </Row>
        <Row label="Value axis"><span className="cs-locked"><CIcon name="lock" size={12} />Starts at 0 (bars always do)</span></Row>
      </Section>
    );
  }
  if (t === "tree") {
    return (
      <Section title="Tree options">
        <Row label="Direction">
          <Seg label="Direction" value={s.config.treeOrient ?? "LR"} onChange={(treeOrient) => setConfig({ treeOrient }, "Tree direction")}
            options={[{ id: "LR", label: "Left to right" }, { id: "RL", label: "Right to left" }, { id: "TB", label: "Top to bottom" }, { id: "BT", label: "Bottom to top" }]} />
        </Row>
        <Row label="Edges">
          <Seg label="Edges" value={s.config.treeEdge ?? "curve"} onChange={(treeEdge) => setConfig({ treeEdge }, "Tree edges")}
            options={[{ id: "curve", label: "Curved" }, { id: "polyline", label: "Polyline" }]} />
        </Row>
      </Section>
    );
  }
  if (t === "clustering") {
    return (
      <Section title="Clusters">
        <Row label="Groups">
          <Seg label="Groups" value={String(s.config.clusters ?? 3) as "2" | "3"} onChange={(k) => setConfig({ clusters: Number(k) as 2 | 3 }, `Clusters: ${k}`)}
            options={[{ id: "2", label: "2" }, { id: "3", label: "3" }]} />
        </Row>
      </Section>
    );
  }
  return null;
}
